// FILE: src/components/BaselineConfidence.js
//
// Phase C — post-first-session overlay (PLAN-phase-a-design.md §3 wireframe,
// multi-cert behavior locked in §6.10.2).
//
// Tone is calm-mentor: strengths first, then where focus will help most, then
// a single recommended next step. Closes with "You're not behind. This is
// just the start of the calibration."
//
// Multi-cert pattern: this overlay covers BOTH active certs:
//   • The cert just taken — full Baseline Confidence layout.
//   • The other active cert — a quiet invitation card ("Curious about
//     <other>? Try a <count>-question intro.") with a synthesized-click
//     hand-off into that cert's practice flow. PMI-ACP is gated and never
//     surfaces here.
//
// Inputs come from the computed readiness result for the taken cert — the
// caller wires this. The overlay does not call services itself; it's pure UI.

import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styles from './BaselineConfidence.module.css';
import CoachLayout from '../coach/CoachLayout';
import { getExamConfig, getAllExams } from '../../config/exams';

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {function} [props.onStartPractice]   — (examId) => void
 * @param {string} props.takenExamId           — 'pmp' | 'cpmai' the learner just took
 * @param {object} props.readiness             — full computeReadiness() result
 * @param {string|null} [props.displayName]
 */
export default function BaselineConfidence({
  isOpen,
  onClose,
  onStartPractice,
  takenExamId,
  readiness,
  displayName,
}) {
  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const examLabel = useMemo(() => {
    const cfg = takenExamId ? getExamConfig(takenExamId) : null;
    return cfg?.displayName || (takenExamId || '').toUpperCase();
  }, [takenExamId]);

  // The "other cert" invitation — picks the first non-coming-soon cert that
  // isn't the one just taken. Returns null if there isn't one.
  const otherCert = useMemo(() => {
    try {
      const all = getAllExams();
      const candidate = all.find((e) => e.examId !== takenExamId && !e.comingSoon);
      if (!candidate) return null;
      const cfg = getExamConfig(candidate.examId);
      return {
        examId:      candidate.examId,
        displayName: candidate.displayName,
        practiceCount: cfg?.practiceExam?.defaultQuestions || 20,
        practiceMinutes: cfg?.practiceExam?.durationMinutes || 30,
      };
    } catch (_) { return null; }
  }, [takenExamId]);

  // All hooks must run unconditionally (rules-of-hooks) BEFORE any early
  // return. We derive these even when the overlay is closed; React skips the
  // tree because the early return below short-circuits the render.
  const phaseList = useMemo(() => derivePhaseList(readiness), [readiness]);
  const nextStep  = useMemo(() => pickNextStep(readiness, takenExamId), [readiness, takenExamId]);

  if (!isOpen) return null;

  // Surface strengths/focus from the readiness coverage breakdown.
  const greeting  = displayName ? `Baseline set — nice start, ${displayName}.` : 'Baseline set — nice start.';

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="baseline-confidence-backdrop"
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="baseline-title"
      >
        <button
          type="button"
          className={styles.closeBtn}
          aria-label="Close baseline summary"
          onClick={() => onClose?.()}
          data-testid="baseline-confidence-close"
        >
          ×
        </button>

        {/* Coaching moment — PM Pixel reviews the learner's baseline alongside
            the summary (notepad/clipboard pose). Reads as part of the coaching
            feedback rather than decoration. */}
        <CoachLayout pose="notepad" heightPx={170}>
          <p className={styles.eyebrow}>Your Certification Journey</p>
          <h2 id="baseline-title" className={styles.title}>{greeting}</h2>

          <p className={styles.subtitle}>
            Here&apos;s where you&apos;re beginning your {examLabel} journey:
          </p>
        </CoachLayout>

        {phaseList.strengths.length > 0 && (
          <section className={styles.section} data-testid="baseline-strengths">
            <h3 className={styles.sectionHeading}>Strengths to build on</h3>
            <ul className={styles.list}>
              {phaseList.strengths.map((p) => (
                <li key={p.phase} className={styles.listItem}>
                  <span className={styles.check} aria-hidden="true">✓</span>
                  <span className={styles.phaseName}>{p.phase}</span>
                  <span className={styles.phasePct}>{p.accuracy}%</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {phaseList.focus.length > 0 && (
          <section className={styles.section} data-testid="baseline-focus">
            <h3 className={styles.sectionHeading}>Where focus will help most</h3>
            <ul className={styles.list}>
              {phaseList.focus.map((p) => (
                <li key={p.phase} className={styles.listItem}>
                  <span className={styles.dot} aria-hidden="true">·</span>
                  <span className={styles.phaseName}>{p.phase}</span>
                  <span className={styles.phasePctMuted}>{p.accuracy}%</span>
                </li>
              ))}
            </ul>
            {phaseList.focus.length > 0 && (
              <p className={styles.coachingNote}>
                This is normal — these phases are most learners&apos; first focus areas.
              </p>
            )}
          </section>
        )}

        {nextStep && (
          <section className={styles.section} data-testid="baseline-next">
            <h3 className={styles.sectionHeading}>A good next step</h3>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => onStartPractice?.(takenExamId, nextStep.focusPhase)}
              data-testid="baseline-next-cta"
            >
              {nextStep.label}  →
            </button>
            <p className={styles.coachingNote}>{nextStep.hint}</p>
          </section>
        )}

        {otherCert && (
          <section className={styles.section} data-testid="baseline-other-cert">
            <div className={styles.otherCertCard}>
              <h3 className={styles.otherCertTitle}>
                Curious about {otherCert.displayName}?
              </h3>
              <p className={styles.otherCertCopy}>
                {otherCert.examId === 'cpmai'
                  ? "PMI's CPMAI covers AI for project managers — a natural pair if you work near AI initiatives."
                  : `${otherCert.displayName} is a parallel certification track worth a look.`}
                {' '}Try a {otherCert.practiceCount}-question intro (~{otherCert.practiceMinutes} min).
              </p>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => onStartPractice?.(otherCert.examId, null)}
                data-testid="baseline-other-cta"
              >
                Start a {otherCert.displayName} intro
              </button>
            </div>
          </section>
        )}

        <p className={styles.footnote}>
          You&apos;re not behind. This is just the start of the calibration —
          readiness will sharpen over the next few sessions.
        </p>

        <div className={styles.continueRow}>
          <button
            type="button"
            className={styles.continueBtn}
            onClick={() => onClose?.()}
            data-testid="baseline-continue"
          >
            Continue to your Journey  ›
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Pulls phase rows out of the readiness coverage breakdown and splits into
 * "strengths" (≥ 70% with ≥ 3 attempts) and "focus" (the weakPhases list).
 */
function derivePhaseList(readiness) {
  if (!readiness) return { strengths: [], focus: [] };
  // The readiness service returns weakPhases at the top of the result; for
  // strengths we synthesize from any session-level aggregation that's
  // exposed in the next phase. For now, build strengths from the inverse
  // selection (phases that appear in readiness.inputs but are NOT in the
  // weakPhases list AND have decent accuracy).
  const weak = Array.isArray(readiness.weakPhases) ? readiness.weakPhases : [];
  const weakSet = new Set(weak.map((w) => w.phase));

  // We don't have full per-phase aggregates in the result shape; derive
  // strengths from a separate optional `inputs.phase_coverage.evidence.phases`
  // field if present, else fall back to []
  const rich = readiness?.inputs?.phase_coverage?.evidence?.phase_table || null;
  const strengths = [];
  if (Array.isArray(rich)) {
    for (const r of rich) {
      if (!weakSet.has(r.phase) && r.total >= 3 && r.accuracy >= 70) {
        strengths.push({ phase: r.phase, accuracy: Math.round(r.accuracy) });
      }
    }
  }

  const focus = weak.slice(0, 3).map((w) => ({ phase: w.phase, accuracy: w.accuracy }));
  return { strengths: strengths.slice(0, 3), focus };
}

/**
 * Pick a sensible "next step" CTA. If there's a weak phase, point at a
 * targeted session on it. Otherwise propose another practice.
 */
function pickNextStep(readiness, takenExamId) {
  const weak = readiness?.weakPhases || [];
  if (weak.length > 0) {
    const w = weak[0];
    return {
      label: `Targeted practice — ${w.phase} (15 questions)`,
      hint:  `15 focused questions on ${w.phase} typically nudges readiness more than a broader session.`,
      focusPhase: w.phase,
    };
  }
  // Fallback when there's no clear weak phase — just another practice session.
  return {
    label: 'Take another practice session',
    hint:  'Another full practice session will sharpen the readiness signal.',
    focusPhase: null,
  };
}
